import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CountBadge } from 'components/CountBadge';
import { PageHeader } from 'components/PageHeader';
import { EMPTY_VALUE } from 'constants/common';
import { formatRelativeDateTime } from 'utils/date';
import { EmptyNotificationsState } from './EmptyNotificationsState';
import {
  useFetchReportNotifications,
  type ReportNotificationRow,
} from './hooks/useFetchReportNotifications';
import { useVerifyReportNotification } from './hooks/useVerifyReportNotification';

const getColumns = (
  onOpenPatientProfile: (notification: ReportNotificationRow) => void
): ColumnDef<ReportNotificationRow>[] => [
  {
    id: 'patient',
    accessorFn: row => row.patientName,
    header: t('SessionsPage.Table.patient.label', { ns: LOCIZE_NS.INSURANCE }),
    meta: { truncation: { maxWidthClassName: 'max-w-32' } },
    cell: ({ row }): React.ReactElement => (
      <div className="flex flex-col">
        <span className="truncate">{row.original.patientName}</span>
        <span className="text-gray-500">
          {t('NotificationsPage.Table.patient.nif', {
            ns: LOCIZE_NS.INSURANCE,
            nif: row.original.patientNif,
          })}
        </span>
      </div>
    ),
  },
  {
    id: 'uploadedAt',
    accessorFn: row => row.reportAvailableAt,
    header: t('NotificationsPage.Table.uploadedAt.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): string =>
      row.original.reportAvailableAt
        ? formatRelativeDateTime(row.original.reportAvailableAt)
        : EMPTY_VALUE,
  },
  {
    id: 'uploadedBy',
    accessorFn: () => EMPTY_VALUE,
    header: t('AppointmentsTable.facultative.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: (): string => EMPTY_VALUE,
  },
  {
    id: 'recordCode',
    accessorFn: row => row.contractRecordCode ?? EMPTY_VALUE,
    header: t('InsuredPersonsList.Table.fileNumber.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ getValue }): string => String(getValue() ?? EMPTY_VALUE),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }): React.ReactElement => (
      <Button
        variant="terciary"
        size="md"
        icon="trailing"
        onClick={() => void onOpenPatientProfile(row.original)}
      >
        {t('NotificationsPage.Table.actions.viewPatient', { ns: LOCIZE_NS.INSURANCE })}
        <ChevronRight className="size-5" />
      </Button>
    ),
  },
];

export const Notifications = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { mutate: verifyReportNotification } = useVerifyReportNotification();

  const handleOpenPatientProfile = useCallback(
    (notification: ReportNotificationRow): void => {
      verifyReportNotification({
        appointmentId: notification.id,
        patientId: notification.patientId,
      });
    },
    [verifyReportNotification]
  );

  const columns = useMemo(() => getColumns(handleOpenPatientProfile), [handleOpenPatientProfile]);
  const { table, query, pendingCount, isLoading } = useFetchReportNotifications(columns);
  const hasNotifications = (query.data?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={t('Nav.insuranceNotifications', { ns: LOCIZE_NS.COMMON })}
        description={t('NotificationsPage.description')}
        titleAdornment={
          pendingCount > 0 ? (
            <CountBadge size="md" className="-mt-4">
              {pendingCount}
            </CountBadge>
          ) : null
        }
      />
      {isLoading || hasNotifications ? (
        <PaginatedTable<ReportNotificationRow>
          autoPageSize
          table={table}
          columns={columns}
          tableClassName="w-full"
          variant="denim"
          onRowAction={handleOpenPatientProfile}
        />
      ) : (
        <EmptyNotificationsState />
      )}
    </div>
  );
};
